import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { 
  Building2, Users, Target, CheckSquare, Settings, Crown,
  MessageSquare, Mail, Brain, LayoutGrid, BarChart3, Clock,
  FileText, Calendar, Bell, Palette, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const featureConfig = [
  { key: "crm", label: "CRM / Lead Management", icon: Target, category: "Core" },
  { key: "tasks", label: "Task Management", icon: CheckSquare, category: "Core" },
  { key: "reminders", label: "Reminders", icon: Bell, category: "Core" },
  { key: "lead_assignment", label: "Lead Assignment", icon: Users, category: "Core" },
  { key: "sms", label: "SMS Notifications", icon: MessageSquare, category: "Communication" },
  { key: "email_templates", label: "Email Templates", icon: Mail, category: "Communication" },
  { key: "ai_assistant", label: "AI Assistant", icon: Brain, category: "AI & Intelligence" },
  { key: "kanban_view", label: "Kanban / Pipeline View", icon: LayoutGrid, category: "Views & UX" },
  { key: "analytics", label: "Analytics Dashboard", icon: BarChart3, category: "Views & UX" },
  { key: "activity_reports", label: "Activity Reports", icon: FileText, category: "Views & UX" },
  { key: "time_tracking", label: "Time Tracking", icon: Clock, category: "Productivity" },
  { key: "invoicing", label: "Invoicing", icon: FileText, category: "Billing" },
  { key: "calendar_sync", label: "Calendar Integration", icon: Calendar, category: "Integrations" },
  { key: "push_notifications", label: "Push Notifications", icon: Bell, category: "Integrations" },
  { key: "white_label", label: "White Label", icon: Palette, category: "Customization" },
  { key: "custom_branding", label: "Custom Branding", icon: Palette, category: "Customization" },
];

const planColors = {
  starter: 'bg-slate-500/20 text-slate-400',
  pro: 'bg-cyan-500/20 text-cyan-400',
  business: 'bg-violet-500/20 text-violet-400',
  enterprise: 'bg-yellow-500/20 text-yellow-400',
};

export const OrganizationDetailModal = ({ open, onClose, organization, onUpdate }) => {
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const fetchFeatures = useCallback(async () => {
    if (!organization?.id) return;
    try {
      const res = await api.get(`/superadmin/organizations/${organization.id}/features`);
      setFeatures(res.data.features || {});
    } catch (err) {
      toast.error('Failed to load features');
    } finally {
      setLoading(false);
    }
  }, [organization?.id]);

  useEffect(() => {
    if (open && organization) {
      setLoading(true);
      fetchFeatures();
    }
  }, [open, organization, fetchFeatures]);

  const toggleFeature = async (featureKey, enabled) => {
    setSaving(featureKey);
    try {
      await api.put(`/superadmin/organizations/${organization.id}/feature/${featureKey}?enabled=${enabled}`);
      setFeatures(prev => ({ ...prev, [featureKey]: enabled }));
      toast.success(`${featureKey.replace(/_/g, ' ')} ${enabled ? 'enabled' : 'disabled'}`);
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error('Failed to update feature');
    } finally {
      setSaving(null);
    }
  };

  // Group features by category
  const categories = featureConfig.reduce((acc, feature) => {
    if (!acc[feature.category]) acc[feature.category] = [];
    acc[feature.category].push(feature);
    return acc;
  }, {});

  if (!organization) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-accent" />
            {organization.name}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            <Badge className={planColors[organization.plan]}>{organization.plan?.toUpperCase()}</Badge>
            <span className="text-xs">/{organization.slug}</span>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-accent" />
          </div>
        ) : (
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-muted/30 rounded-lg p-3 text-center">
                  <Users className="w-5 h-5 mx-auto text-cyan-500 mb-1" />
                  <p className="text-lg font-bold">{organization.user_count || 0}</p>
                  <p className="text-xs text-muted-foreground">Users</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-3 text-center">
                  <Target className="w-5 h-5 mx-auto text-violet-500 mb-1" />
                  <p className="text-lg font-bold">{organization.lead_count || 0}</p>
                  <p className="text-xs text-muted-foreground">Leads</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-3 text-center">
                  <CheckSquare className="w-5 h-5 mx-auto text-green-500 mb-1" />
                  <p className="text-lg font-bold">{organization.task_count || 0}</p>
                  <p className="text-xs text-muted-foreground">Tasks</p>
                </div>
              </div>

              <Separator />

              {/* Feature Toggles by Category */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Crown className="w-4 h-4 text-yellow-500" />
                  Feature Toggles
                </h3>
                <p className="text-sm text-muted-foreground">
                  Toggle features on/off for this organization. Changes take effect immediately.
                </p>

                {Object.entries(categories).map(([category, categoryFeatures]) => (
                  <div key={category} className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {category}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {categoryFeatures.map((feature) => {
                        const Icon = feature.icon;
                        const isEnabled = features[feature.key] ?? false;
                        const isSaving = saving === feature.key;
                        
                        return (
                          <div 
                            key={feature.key}
                            className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                              isEnabled ? 'bg-accent/10 border-accent/30' : 'bg-muted/30 border-transparent'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <Icon className={`w-4 h-4 ${isEnabled ? 'text-accent' : 'text-muted-foreground'}`} />
                              <span className="text-sm">{feature.label}</span>
                            </div>
                            {isSaving ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Switch
                                checked={isEnabled}
                                onCheckedChange={(checked) => toggleFeature(feature.key, checked)}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </ScrollArea>
        )}

        <div className="flex justify-end pt-4">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
